import { useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { config } from '#/config';
import { getAccessToken } from '#/api/auth';
import logger from "#/utils/logger";

// Global WebSocket instance
let webSocket: WebSocket | null = null;
let isInitializing = false;

type MessageHandler = (data: any) => void;
let messageHandler: MessageHandler | null = null;
type ConnectionHandler = (connected: boolean) => void;
let connectionHandler: ConnectionHandler | null = null;

enum WebsocketMode {
  CommandMode,
  ListenMode,
}

export const WebSocketCommand = {
  LISTEN: "LISTEN",
  SESSION: "SESSION",
  SHIFT_OTHERS_TO_START_SCREEN: "SHIFT_TO_START",
  TERMINATE: "TERMINATE",
  PROCESS: "PROCESS",
  PROMPT_FEEDBACK: "PROMPT_FEEDBACK",
}

export const WebSocketReply = {
  MODE: 'mode',
  CONFIDENCE: 'confidence',
  DETECT: 'detect',
  PROMPT: 'prompt',
  UNRECOGNIZED: 'unrecognized',
  ERROR: 'error',
  FORCE_TO_START_SCREEN: 'force_to_start_screen',
}

interface WebsocketContextType {
  setConnectionHandler: (handler: ConnectionHandler | null) => void;
  sendMessage: (data: string) => void;
  sendAudio: (data: ArrayBufferView<ArrayBufferLike>) => void;
  setCallback: (handler: MessageHandler | null) => void;
  setWebsocketSession: (sessionId: string) => void;
  startListening: () => void;
  stopListening: (cancelRecording: boolean) => void;
}

const WebscoketContext = createContext<WebsocketContextType | undefined>(undefined);

export const WebsocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentMode = useRef<WebsocketMode>(WebsocketMode.CommandMode);
  const currentSession = useRef<string | null>(null);

  useEffect(() => {

    const messageReceived = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type == WebSocketReply.MODE) {
          if (message.payload == "command" && currentMode.current == WebsocketMode.ListenMode) {
            // Backend is waiting for commands and we are sending audio
            logger.info("Wrong backend state detected - switching to listening mode")
            startListening();
          } else if (message.payload == "listen" && currentMode.current == WebsocketMode.CommandMode) {
            // Backend is listening and we are sending commands
            logger.info("Wrong backend state detected - switching to command mode")
            stopListening(true);
          }
        }
        else if (messageHandler) {
          messageHandler(message);
        }
      } catch (error) {
        logger.error("Received invalid message: " + event.data);
      }
    }

    const initWebSocket = async () => {
      if (webSocket?.readyState === WebSocket.OPEN || isInitializing) return;
      isInitializing = true;

      try {
        const jwtToken = await getAccessToken();
        if (typeof jwtToken !== 'string') {
          throw new Error('Failed to get access token');
        }
        const wsUrl = new URL(`${config.wsBackendUrl}/ws`);
        wsUrl.searchParams.append('token', jwtToken);

        const newWebSocket = new WebSocket(wsUrl.toString());
        newWebSocket.binaryType = 'arraybuffer';

        newWebSocket.onopen = () => {
          logger.info('WebSocket connected');
          if (connectionHandler) {
            connectionHandler(true);
          }
          webSocket = newWebSocket;
          isInitializing = false;
          if (currentSession.current) {
            // Send current session ID
            const message = JSON.stringify({
              command: WebSocketCommand.SESSION,
              payload: { session_id: currentSession.current }
            });
            sendMessage(message);
          } else {
            logger.info("Session not set - cannot send on socket open.")
          }
          if (currentMode.current == WebsocketMode.ListenMode) {
            // Move to listen mode immediately
            startListening();
          }
        };

        newWebSocket.addEventListener('message', messageReceived);

        newWebSocket.onclose = () => {
          logger.info('WebSocket disconnected');
          if (connectionHandler) {
            connectionHandler(false);
          }
          webSocket = null;
          isInitializing = false;
          setTimeout(initWebSocket, 5000);
        };

        newWebSocket.onerror = error => {
          logger.error('WebSocket Error:', error);
          isInitializing = false;
        };
      } catch (error) {
        logger.error('Failed to initialize WebSocket:', error);
          if (connectionHandler) {
            connectionHandler(false);
          }
        isInitializing = false;
        setTimeout(initWebSocket, 5000);
      }
    };

    initWebSocket();

    return () => {
    };
  }, []);

  const sendMessage = useCallback((data: string) => {
    if (webSocket?.readyState === WebSocket.OPEN) {
      if (currentMode.current == WebsocketMode.CommandMode) {
        webSocket.send(data);
      } else {
        logger.debug("Trying to send command " + data + " while in listening mode")
      }
    }
  }, []);

  const setWebsocketSession = useCallback((sessionId: string) => {
    if (!currentSession.current && webSocket?.readyState === WebSocket.OPEN) {
      // Send session info
      const message = JSON.stringify({
        command: WebSocketCommand.SESSION,
        payload: { session_id: sessionId }
      });
      sendMessage(message);
    }
    currentSession.current = sessionId;
  }, []);

  const sendAudio = useCallback((data: ArrayBufferView<ArrayBufferLike>) => {
    if (webSocket?.readyState === WebSocket.OPEN) {
      // Audio is never retried
      if (currentMode.current == WebsocketMode.ListenMode) {
        webSocket.send(data);
      } else {
        logger.debug("Trying to send audio when not in listening mode")
      }
    }
  }, []);

  // Start recording on socket
  const startListening = useCallback(() => {
    if (webSocket?.readyState === WebSocket.OPEN) {
        logger.debug(`Sending message start recording message`)
        webSocket.send(JSON.stringify({ command: WebSocketCommand.LISTEN }));
    } else {
      logger.debug("Websocket not open - cannot start listening immediately")
    }

    // Move to listening mode
    currentMode.current = WebsocketMode.ListenMode;
  }, []);

  // Stop recording. If cancel, do not process the audio
  const stopListening = useCallback((cancelRecording: boolean = false) => {
    if (webSocket?.readyState === WebSocket.OPEN) {
        if (cancelRecording) {
          logger.debug(`Sending TERMINATE message`)
          webSocket.send(new TextEncoder().encode(WebSocketCommand.TERMINATE));
        } else {
          logger.debug(`Sending PROCESS message`)
          webSocket.send(new TextEncoder().encode(WebSocketCommand.PROCESS));
        }
    }
    // Move to command mode
    currentMode.current = WebsocketMode.CommandMode;
  }, []);

  const setCallback = useCallback((handler: MessageHandler | null) => {
    messageHandler = handler;
  }, []);

  const setConnectionHandler = useCallback((handler: ConnectionHandler | null) => {
    connectionHandler = handler;
  }, []);

  return (
    <WebscoketContext.Provider value={{ setConnectionHandler, sendMessage, sendAudio, setCallback,
                                        setWebsocketSession, startListening, stopListening }}>
      {children}
    </WebscoketContext.Provider>
  );
};


export const useWebSocket = () => {
  const context = useContext(WebscoketContext);
  if (context === undefined) {
    throw new Error('useWebsocket must be used within a WebsocketContext Provider');
  }
  return context;
};